import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagesTab = ({ currentUser, getAuthConfig, pendingMessageTarget, clearPendingTarget }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageRecipient, setMessageRecipient] = useState(null);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`, getAuthConfig());
      setConversations(response.data);
    } catch (e) { console.error('Messages fetch error:', e); }
  };

  useEffect(() => { fetchConversations(); }, []);

  useEffect(() => {
    if (pendingMessageTarget && currentUser) {
      setMessageRecipient(pendingMessageTarget);
      const pair = [currentUser.user_id, pendingMessageTarget].sort();
      const convId = `conv_${pair[0]}_${pair[1]}`;
      const existingConv = conversations.find(c => c.conversation_id === convId);
      if (existingConv) {
        openConversation(convId);
      } else {
        setActiveConversation(null);
        setConversationMessages([]);
      }
      clearPendingTarget();
    }
  }, [pendingMessageTarget, currentUser]);

  const openConversation = async (conversationId) => {
    setActiveConversation(conversationId);
    try {
      const response = await axios.get(`${API}/messages/${conversationId}`, getAuthConfig());
      setConversationMessages(response.data);
      fetchConversations();
    } catch (error) { console.error('Messages error:', error); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    const toUserId = messageRecipient || (() => {
      const conv = conversations.find(c => c.conversation_id === activeConversation);
      if (!conv) return null;
      const msg = conv.last_message;
      return msg.from_user_id === currentUser?.user_id ? msg.to_user_id : msg.from_user_id;
    })();
    if (!toUserId) return;
    try {
      await axios.post(`${API}/messages`, { to_user_id: toUserId, content: newMessage }, getAuthConfig());
      setNewMessage('');
      if (activeConversation) openConversation(activeConversation);
      else {
        fetchConversations();
        const pair = [currentUser.user_id, toUserId].sort();
        const convId = `conv_${pair[0]}_${pair[1]}`;
        setActiveConversation(convId);
        openConversation(convId);
      }
      setMessageRecipient(null);
    } catch (error) { alert(error.response?.data?.detail || 'Failed to send message'); }
  };

  const unreadCount = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  return (
    <div className="messages-section" data-testid="messages-section" style={{ display: 'flex', gap: '20px', minHeight: '500px' }}>
      <div style={{ width: '280px', flexShrink: 0, background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', fontWeight: '700', color: '#1a2332' }}>Conversations</div>
        {conversations.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>No messages yet.<br/>Click "Message" on a member to start.</div>
        ) : (
          conversations.map(conv => {
            const msg = conv.last_message;
            const otherName = msg.from_user_id === currentUser?.user_id ? msg.to_name : msg.from_name;
            return (
              <div key={conv.conversation_id} onClick={() => openConversation(conv.conversation_id)}
                style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', background: activeConversation === conv.conversation_id ? '#eff6ff' : 'white' }}
                data-testid={`conv-${conv.conversation_id}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '14px' }}>{otherName}</strong>
                  {conv.unread_count > 0 && <span style={{ background: '#ef4444', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '11px' }}>{conv.unread_count}</span>}
                </div>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.content}</p>
              </div>
            );
          })
        )}
      </div>
      <div style={{ flex: 1, background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column' }}>
        {activeConversation || messageRecipient ? (
          <>
            <div style={{ flex: 1, padding: '16px', overflowY: 'auto', maxHeight: '400px' }}>
              {conversationMessages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px' }}>Start the conversation!</div>
              ) : (
                conversationMessages.map(msg => (
                  <div key={msg.message_id} style={{ marginBottom: '12px', display: 'flex', justifyContent: msg.from_user_id === currentUser?.user_id ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '70%', padding: '10px 14px', borderRadius: '12px',
                      background: msg.from_user_id === currentUser?.user_id ? '#2563eb' : '#f3f4f6',
                      color: msg.from_user_id === currentUser?.user_id ? 'white' : '#1a2332'
                    }} data-testid={`msg-${msg.message_id}`}>
                      <p style={{ margin: 0, fontSize: '14px' }}>{msg.content}</p>
                      <p style={{ margin: '4px 0 0', fontSize: '10px', opacity: 0.7 }}>{new Date(msg.created_at).toLocaleTimeString()}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={sendMessage} style={{ display: 'flex', gap: '8px', padding: '12px', borderTop: '1px solid #e5e7eb' }}>
              <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type a message..."
                style={{ flex: 1, padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }} data-testid="message-input" />
              <button type="submit" className="btn btn-primary" style={{ padding: '10px 20px' }} data-testid="send-message-btn">Send</button>
            </form>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#9ca3af' }}>
            Select a conversation or message a member to start chatting
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesTab;
